export type FileType = "jpeg" | "png" | "heic" | "pdf" | "excel" | "word" | "powerpoint";
export type FileConfigType =
  | "сonsultantImport"
  | "consultantContract"
  | "images"
  | "email"
  | "default";

const maxFileSize = 5 * 1024 * 1024; // 5MB in bytes

export const fileTypes = new Map<FileType, { mimetype: string; ext: string }>([
  ["jpeg", { mimetype: "image/jpeg", ext: "jpeg,jpg" }],
  ["png", { mimetype: "image/png", ext: "png" }],
  ["heic", { mimetype: "image/heic", ext: "heic" }],
  ["pdf", { mimetype: "application/pdf", ext: "pdf" }],
  [
    "excel",
    {
      mimetype:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel",
      ext: "xls,xlsx"
    }
  ],
  [
    "word",
    {
      mimetype:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword",
      ext: "doc,docx"
    }
  ],
  [
    "powerpoint",
    {
      mimetype:
        "application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-powerpoint",
      ext: "ppt,pptx"
    }
  ]
]);

const fileConfigs = new Map<
  FileConfigType,
  { fileTypes: FileType[]; maxFileSize: number }
>([
  [
    "сonsultantImport",
    {
      fileTypes: ["excel"],
      maxFileSize
    }
  ],
  [
    "default",
    {
      fileTypes: ["jpeg", "png", "heic", "pdf", "excel", "word", "powerpoint"],
      maxFileSize
    }
  ],
  [
    "images",
    {
      fileTypes: ["jpeg", "png", "heic"],
      maxFileSize
    }
  ],
  [
    "email",
    {
      fileTypes: ["jpeg", "png", "heic", "pdf", "excel", "word", "powerpoint"],
      maxFileSize: 1024 * 1024 * 4 // 4MB
    }
  ],
  [
    "consultantContract",
    {
      fileTypes: ["pdf"],
      maxFileSize
    }
  ]
]);

export class FileUploadConfig {
  allowedTypes: string[];
  acceptString: string;
  maxFileSize: number;

  mimeTypes: Map<string, string>;
  acceptExts: string[];
  acceptTypes: Record<string, string[]>;

  constructor(type: FileConfigType = "default") {
    const config = fileConfigs.get(type);

    if (!config) {
      throw new Error(`File config not found for key: ${type}`);
    }

    const allowedTypes: string[] = [];
    const acceptExts: string[] = [];
    const acceptTypes: Record<string, string[]> = {};

    const mimeTypes = new Map<string, string>();

    for (const enumKey of config.fileTypes) {
      const fileTypeConfig = fileTypes.get(enumKey);
      const exts = fileTypeConfig.ext.split(",");
      const types = fileTypeConfig.mimetype.split(",");

      for (const type of types) {
        mimeTypes.set(type, fileTypeConfig.ext);
        acceptTypes[type] = exts.map((ext) => `.${ext}`);
        allowedTypes.push(type);

        if (type === "image/heic") {
          // for platforms that don't support file types (heic) out of the box (eg. windows)
          acceptTypes[""] = [`.${fileTypeConfig.ext}`];
        }
      }

      acceptExts.push(...exts);
    }

    this.allowedTypes = allowedTypes;
    this.mimeTypes = mimeTypes;
    this.acceptTypes = acceptTypes;
    this.acceptExts = acceptExts;
    this.acceptString = acceptExts.map((ext) => `.${ext}`).join(", ");
    this.maxFileSize = config.maxFileSize;
  }

  allowType(type: string, filename: string) {
    const ext = filename ? getFileExtension(filename) : null;

    return (
      this.allowedTypes.includes(type) ||
      // for platforms that don't support file types (heic) out of the box (eg. windows)
      (ext === "heic" ? this.acceptExts.includes(ext) : false)
    );
  }

  getExtensionFromMimeType(mimeType: string): string | null {
    const ext = this.mimeTypes.get(mimeType);

    if (!ext) {
      return null;
    }

    // get last extension
    return ext.split(",").at(-1);
  }
}

/**
 * Exact file extension -> mime type (CRMDEV-7150).
 *
 * `fileTypes` keeps a family's mime types in one comma-joined string and its
 * extensions in another, and the two lists are not in the same order: `.doc`
 * is the legacy `application/msword` while `.docx` is the OOXML type, which is
 * listed first. Neither the joined string nor a positional pairing can answer
 * "what is the mime type of a .doc", so the mapping is spelled out here.
 * `file-config.spec.ts` asserts it stays in sync with `fileTypes`.
 */
export const extensionMimeTypes = new Map<string, string>([
  ["jpeg", "image/jpeg"],
  ["jpg", "image/jpeg"],
  ["png", "image/png"],
  ["heic", "image/heic"],
  ["pdf", "application/pdf"],
  ["xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  ["xls", "application/vnd.ms-excel"],
  ["docx", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  ["doc", "application/msword"],
  ["pptx", "application/vnd.openxmlformats-officedocument.presentationml.presentation"],
  ["ppt", "application/vnd.ms-powerpoint"]
]);

/**
 * Mime type for a file extension, or `""` when the extension is unknown.
 *
 * Returns a *single* mime type: the previous implementation returned the raw
 * comma-joined `fileTypes` value, which `FileUploadConfig` splits on `,` when
 * it builds `allowedTypes`, so the joined string was never allowed and every
 * Word / Excel / PowerPoint upload threw in `CrmDocumentEntity` (CRMDEV-7150).
 *
 * The lookup is exact. It used to be `value.ext.includes(ext)`, a substring
 * match on the joined extension list, so `""` matched `"jpeg,jpg"` and stamped
 * every extensionless file `image/jpeg`, and `"ls"` matched `"xls,xlsx"`.
 */
export const extensionToMimeType = (ext: string): string => {
  if (!ext) return "";

  return extensionMimeTypes.get(ext.trim().toLowerCase().replace(/^\.+/, "")) ?? "";
};

export function getMimeType(type: FileType): string {
  return fileTypes.get(type)?.mimetype || "";
}

export function getFileExtension(name: string): string {
  const parts = name.split(".");

  if (parts.length > 1) {
    return parts.pop().toLowerCase();
  }

  return "";
}

export function getFileNameFromUri(uri: string): string {
  const regex = /\/([^/]+)?\?(?=[\w=&])/;
  const match = uri.match(regex);
  return match && match[1] ? match[1] : null;
}
